import Decimal from 'decimal.js';
import { getActiveMarket } from '../config/markets.config';
import { CreateOfferData } from '../models/create-offer/create-offer-data';
import { ManualVariantSelection } from '../models/create-offer/filter-data';
import { Filter, FilterValueType } from '../models/filter';
import { CreateOfferPackage, Offer, OfferInterface, OfferVariantGroup, OfferVariantInterface, OfferWholesaleLine } from '../models/offer';
import {
    OfferDefinition,
    OfferVariant,
    OfferVariantPost,
    OfferVariantValuePost,
    PostOfferVariantDefinition,
} from '../models/offer-definition';
import { User } from '../models/user';
import { CategoryUtils } from './category.utils';
import { OfferUtils } from './offer.utils';

/** Map missing/falsy VAT form values (e.g. 0 from +null) to null for API payloads. */
export function normalizeVatId(vat: number | null | undefined): number | null {
    return vat || null;
}

// ---------------------------------------------------------------------------
// Seeding helpers: read an existing Offer into FilterData / CreateOfferData
// ---------------------------------------------------------------------------

/**
 * Build filterValues map (filter code -> raw value) from offer-level definitions only.
 */
export function buildFilterValuesFromOfferDefinitions(offer: Offer, filters: Filter[]): Record<string, FilterValueType> {
    const byFilterId = new Map<number, OfferDefinition[]>();
    for (const d of offer.definitions || []) {
        const list = byFilterId.get(d.filter) || [];
        list.push(d);
        byFilterId.set(d.filter, list);
    }

    const out: Record<string, FilterValueType> = {};
    // Gazette listings have no declinations. Variant-type filters are saved on the
    // offer and edited as ordinary details. Marketplace and wholesale keep those
    // values on variants so they are not copied into the details form.
    const variantFiltersStayOnVariants = OfferUtils.canHaveVariants(offer.platform);
    for (const filter of filters) {
        if (filter.canCreateVariant && variantFiltersStayOnVariants) {
            continue;
        }
        const defs = byFilterId.get(filter.id);
        if (!defs?.length) {
            continue;
        }
        const d = defs[0];
        switch (filter.type) {
            case 'boolean':
                out[filter.getCode()] = d.integerValue === 1;
                break;
            case 'text':
                out[filter.getCode()] = d.textValue;
                break;
            case 'decimal':
                out[filter.getCode()] = d.integerValue != null ? d.integerValue / 100 : null;
                break;
            case 'integer':
            case 'year':
                out[filter.getCode()] = d.integerValue;
                break;
            case 'date':
                out[filter.getCode()] = d.dateValue;
                break;
            case 'single_choice': {
                const fv = filter.filterValues.find((v) => v.id === d.filterValues?.[0]);
                if (fv) {
                    out[filter.getCode()] = fv.code;
                }
                break;
            }
            case 'multi_choice':
            case 'opening_hours': {
                const ids = new Set<number>();
                for (const def of defs) {
                    for (const id of def.filterValues || []) {
                        ids.add(id);
                    }
                }
                const codes = [...ids].map((id) => filter.filterValues.find((v) => v.id === id)?.code).filter((c): c is string => !!c);
                if (codes.length) {
                    out[filter.getCode()] = codes;
                }
                break;
            }
            case 'composition': {
                const code = filter.getCode();
                const compositionValue: Record<string, { percentage: number; value: string[] }> = {};
                defs.forEach((def, index) => {
                    const fvCode = filter.filterValues.find((v) => v.id === def.filterValues?.[0])?.code;
                    if (!fvCode) {
                        return;
                    }
                    compositionValue[`${code}_comp_${index}`] = {
                        percentage: def.integerValue,
                        value: [fvCode],
                    };
                });
                if (Object.keys(compositionValue).length) {
                    out[code] = compositionValue;
                }
                break;
            }
            default:
                break;
        }
    }
    return out;
}

/**
 * Build variant form patch: filter code -> value(s) compatible with pick-filters / OfferDefinition.createAll.
 */
export function buildVariantFilterValuesPatch(offer: Offer, filters: Filter[]): Record<string, FilterValueType | FilterValueType[]> {
    const variantFilters = filters.filter((f) => f.canCreateVariant);
    if (!variantFilters.length || !offer.variants?.length) {
        return {};
    }
    const patch: Record<string, FilterValueType | FilterValueType[]> = {};

    for (const filter of variantFilters) {
        const code = filter.getCode();
        if (filter.type === 'single_choice' || filter.type === 'multi_choice') {
            const row: string[] = [];
            for (const v of offer.variants) {
                const def = v.definitions.find((d) => d.filter === filter.id);
                const fv = def?.filterValues?.[0];
                const fvc = fv != null ? filter.filterValues.find((x) => x.id === fv)?.code : null;
                if (fvc) {
                    row.push(fvc);
                }
            }
            if (row.length) {
                patch[code] = row;
            }
        } else {
            const row: FilterValueType[] = [];
            for (const v of offer.variants) {
                const def = v.definitions.find((d) => d.filter === filter.id);
                if (!def) {
                    continue;
                }
                let cell: FilterValueType = null;
                switch (filter.type) {
                    case 'boolean':
                        cell = def.integerValue === 1;
                        break;
                    case 'text':
                        cell = def.textValue;
                        break;
                    case 'decimal':
                        cell = def.integerValue != null ? def.integerValue / 100 : null;
                        break;
                    case 'integer':
                    case 'year':
                        cell = def.integerValue;
                        break;
                    case 'date':
                        cell = def.dateValue;
                        break;
                    default:
                        break;
                }
                row.push(cell);
            }
            if (row.length) {
                patch[code] = row;
            }
        }
    }
    return patch;
}

export function buildVariantPostsFromOffer(offer: Offer, filters: Filter[]): (OfferVariantPost & { sourceVariantId?: number })[] {
    return offer.variants.map((v) => {
        const post = new OfferVariantPost() as OfferVariantPost & { sourceVariantId?: number };
        post.sourceVariantId = v.id;
        post.definitions = definitionsInSelectorOrder(v.definitions, filters).map((d) => {
            const filter = filters.find((f) => f.id === d.filter) || d.filterEntity;
            const fvId = d.filterValues?.[0];
            const integerValue = filter?.type === 'decimal' && d.integerValue != null ? d.integerValue / 100 : d.integerValue;
            const value = new OfferVariantValuePost({
                filterValue: fvId,
                filterValueEntity:
                    filter?.filterValues?.find((fv) => fv.id === fvId) ?? d.filterValueEntities?.find((fv) => fv.id === fvId),
                integerValue,
                textValue: d.textValue,
            });
            return new PostOfferVariantDefinition({
                filter: d.filter,
                filterEntity: filter,
                value,
            });
        });
        return post;
    });
}

/**
 * Offer-group selector codes are `groupFilter` first, then the other variant
 * filters in category order. Seeded posts use that same order so a later
 * Details submit can match them by `code`.
 */
function definitionsInSelectorOrder(definitions: OfferDefinition[], filters: Filter[]): OfferDefinition[] {
    const variantFilters = filters.filter((filter) => filter.canCreateVariant);
    const groupFilter = variantFilters.find((filter) => filter.canCreateOfferGroup);
    const orderedFilters = [...(groupFilter ? [groupFilter] : []), ...variantFilters.filter((filter) => filter !== groupFilter)];
    const used = new Set<OfferDefinition>();
    const ordered: OfferDefinition[] = [];
    for (const filter of orderedFilters) {
        const definition = definitions.find((item) => item.filter === filter.id && !used.has(item));
        if (!definition) {
            continue;
        }
        ordered.push(definition);
        used.add(definition);
    }
    for (const definition of definitions) {
        if (!used.has(definition)) {
            ordered.push(definition);
        }
    }
    return ordered;
}

/**
 * One offer is one color (or other offer-group value) and its sizes.
 * Sibling offers in the same offer group are not in `offer.variants`.
 * Edit-only: publish keeps using the selection `submit()` already stored.
 */
export function buildManualVariantSelectionFromOffer(offer: Offer, filters: Filter[]): ManualVariantSelection | null {
    const variantFilters = filters.filter((filter) => filter.canCreateVariant);
    const groupFilter = variantFilters.find((filter) => filter.canCreateOfferGroup);
    if (!groupFilter || !offer.variants?.length) {
        return null;
    }
    const otherFilters = variantFilters.filter((filter) => filter !== groupFilter);
    let groupValue: unknown = null;
    const grouped: { [code: string]: unknown[] } = {};

    for (const variant of offer.variants) {
        const groupDefinition = variant.definitions.find((definition) => definition.filter === groupFilter.id);
        const value = selectionValueFromDefinition(groupFilter, groupDefinition);
        if (value == null) {
            continue;
        }
        if (groupValue == null) {
            groupValue = value;
        }
        if (selectionValueKey(groupValue) !== selectionValueKey(value)) {
            continue;
        }
        for (const filter of otherFilters) {
            const definition = variant.definitions.find((item) => item.filter === filter.id);
            const cell = selectionValueFromDefinition(filter, definition);
            if (cell == null) {
                continue;
            }
            const code = filter.getCode();
            const list = grouped[code] ?? [];
            if (!list.some((existing) => selectionValueKey(existing) === selectionValueKey(cell))) {
                list.push(cell);
            }
            grouped[code] = list;
        }
    }

    if (groupValue == null) {
        return null;
    }

    return {
        selectedValues: [groupValue],
        variantSelections: {
            [selectionValueKey(groupValue)]: grouped,
        },
    };
}

/**
 * The single group this offer belongs to, so Photos can show `offer.medias` on it.
 * Callers also keep those medias on `mediaData`.
 */
export function buildEditOfferVariantGroup(
    offer: Offer,
    filters: Filter[],
    selection: ManualVariantSelection | null,
    variants: OfferVariantPost[],
): OfferVariantGroup | null {
    if (!selection?.selectedValues?.length) {
        return null;
    }
    const groupFilter = filters.find((filter) => filter.canCreateVariant && filter.canCreateOfferGroup);
    if (!groupFilter) {
        return null;
    }
    const selected = selection.selectedValues[0];
    const value = offerVariantValueFromSelection(groupFilter, selected);
    if (!value) {
        return null;
    }
    return {
        code: `${groupFilter.getCode()}_${selectionValueKey(selected)}`,
        filter: groupFilter,
        value,
        variants,
        medias: [],
    };
}

function selectionValueKey(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (value && typeof value === 'object' && 'value' in value && (value as { value?: unknown }).value !== undefined) {
        return String((value as { value: unknown }).value);
    }
    return String(value);
}

function selectionValueFromDefinition(filter: Filter, definition: OfferDefinition | undefined): unknown | null {
    if (!definition) {
        return null;
    }
    switch (filter.type) {
        case 'single_choice':
        case 'multi_choice': {
            const id = definition.filterValues?.[0];
            if (id == null) {
                return null;
            }
            return (
                filter.filterValues.find((value) => value.id === id)?.code ??
                definition.filterValueEntities?.find((value) => value.id === id)?.code ??
                null
            );
        }
        case 'decimal':
            if (definition.integerValue == null) {
                return null;
            }
            return { value: String(definition.integerValue / 100) };
        case 'integer':
        case 'year':
            if (definition.integerValue == null) {
                return null;
            }
            return { value: String(definition.integerValue) };
        case 'boolean':
            if (definition.integerValue == null) {
                return null;
            }
            return { value: definition.integerValue === 1 ? '1' : '0' };
        case 'text':
            if (definition.textValue == null || definition.textValue === '') {
                return null;
            }
            return { value: definition.textValue };
        case 'date':
            if (!definition.dateValue) {
                return null;
            }
            return { value: definition.dateValue };
        case 'composition':
        case 'opening_hours':
            return null;
        default: {
            const _exhaustive: never = filter.type;
            return _exhaustive;
        }
    }
}

function offerVariantValueFromSelection(filter: Filter, selected: unknown): OfferVariantValuePost | null {
    if (typeof selected === 'string') {
        const filterValue = filter.filterValues.find((value) => value.code === selected);
        if (!filterValue) {
            return null;
        }
        return new OfferVariantValuePost({
            filterValue: filterValue.id,
            filterValueEntity: filterValue,
        });
    }
    if (selected && typeof selected === 'object' && 'value' in selected && (selected as { value?: unknown }).value !== undefined) {
        const formatted = filter.formatValue((selected as { value: unknown }).value);
        if (typeof formatted === 'number') {
            return new OfferVariantValuePost({ integerValue: formatted });
        }
        return new OfferVariantValuePost({ textValue: String(formatted) });
    }
    return null;
}

/**
 * B2B form fields: the `min === 1` wholesale line is the sample price (its max is
 * the order threshold) and the remaining lines are the grid.
 * Edit-only. `referenceCurrency` is left to the caller.
 */
export function applyWholesalePriceFieldsFromOffer(
    priceData: {
        samplePrice: string;
        minWholesaleQuantity: number;
        wholesalePrices: { min: number; max: number; price: string }[];
    },
    offer: Offer,
): void {
    if (offer.platform !== 'wholesale') {
        return;
    }
    const lines = offer.wholesalePrices ?? [];
    const sampleIndex = lines.findIndex((line) => Number(line.min) === 1);
    const sample = sampleIndex >= 0 ? lines[sampleIndex] : undefined;
    if (sample) {
        priceData.samplePrice = sample.price;
    }
    priceData.minWholesaleQuantity = offer.minWholesaleQuantity;
    priceData.wholesalePrices = lines
        .filter((_, index) => index !== sampleIndex)
        .map((line) => ({
            min: line.min,
            max: line.max,
            price: line.price,
        }));
}

/**
 * Copy `sourceVariantId` onto regenerated variants that kept the same code.
 * Create-flow variants have no id, so this is a no-op for publish.
 */
export function copySourceVariantIds(
    previous: readonly (OfferVariantPost & { sourceVariantId?: number })[] | null | undefined,
    next: (OfferVariantPost & { sourceVariantId?: number })[] | null | undefined,
): void {
    const idsByCode = new Map<string, number>();
    for (const variant of previous ?? []) {
        if (variant.sourceVariantId != null && variant.code) {
            idsByCode.set(variant.code, variant.sourceVariantId);
        }
    }
    for (const variant of next ?? []) {
        const sourceVariantId = variant.code ? idsByCode.get(variant.code) : undefined;
        if (sourceVariantId != null) {
            variant.sourceVariantId = sourceVariantId;
        }
    }
}

/**
 * Seed priceData.variants (keyed by variant code) from an existing offer so
 * that PickPrices can seed its form entirely from `data` on the edit flow.
 */
export function buildPriceVariantsMapFromOffer(
    offerVariants: OfferVariant[],
    posts: (OfferVariantPost & { sourceVariantId?: number })[],
): { [code: string]: { price: string; stocks: number; sku: string } } {
    const map: { [code: string]: { price: string; stocks: number; sku: string } } = {};
    for (const post of posts) {
        const source = offerVariants.find((v) => v.id === post.sourceVariantId);
        if (!source) {
            continue;
        }
        map[post.code] = {
            price: source.price,
            stocks: source.stocks,
            sku: source.sku,
        };
    }
    return map;
}

/**
 * Seed priceData.packages from an existing offer.
 */
export function buildPackagesFromOffer(offer: Offer): CreateOfferPackage[] {
    return (offer.packages || []).map((p) => ({
        weight: p.weight,
        deci: p.deci != null ? new Decimal(p.deci as unknown as string).toFixed(2) : '0.00',
    }));
}

// ---------------------------------------------------------------------------
// Payload helpers: read CreateOfferData -> API body shapes
// ---------------------------------------------------------------------------

export function normalizeCurrency(value: unknown): string | null {
    if (Array.isArray(value)) {
        return (value[0] as string) ?? null;
    }
    return (value as string) ?? null;
}

export function buildMediaIds(data: CreateOfferData, group: OfferVariantGroup | null = null): number[] {
    if (group) {
        return (group.medias || []).map((m) => m.id);
    }
    return (data.mediaData?.medias || []).map((m) => m.id);
}

export function buildPackagesPayload(data: CreateOfferData): CreateOfferPackage[] {
    return (data.priceData?.packages || []).map((p) => ({
        weight: p.weight,
        deci: new Decimal(p.deci || 0).toFixed(2),
    }));
}

/**
 * Build the shared definitions payload: offer-level definitions + variant-derived values,
 * matching the aggregation used in the create flow.
 */
export function buildDefinitionsPayload(
    data: CreateOfferData,
    variants: OfferVariantPost[] = null,
): Array<{
    filter: number;
    textValue?: string;
    integerValue?: number;
    filterValues?: number[];
    dateValue?: string;
}> {
    const filters = data.filterData.filters;
    const actualVariants = variants ?? data.filterData.variants ?? [];
    const offerLevel = OfferDefinition.createAll(filters, data.filterData.filterValues);

    const values: { [key: string]: FilterValueType } = {};
    for (const variant of actualVariants) {
        for (const definition of variant.definitions) {
            const filterCode = definition.filterEntity.getCode();
            const value = definition.rawValue;
            if (definition.filterEntity.type === 'single_choice' || definition.filterEntity.type === 'multi_choice') {
                const array: (string | number | boolean)[] = (values[filterCode] as (string | number | boolean)[]) || [];
                if (!array.includes(value as string | number | boolean)) {
                    array.push(value as string | number | boolean);
                }
                values[filterCode] = array as FilterValueType;
            } else if (!values[filterCode]) {
                values[filterCode] = value;
            }
        }
    }

    const merged = [...offerLevel, ...OfferDefinition.createAll(filters, values)];
    return merged.map((d) => ({
        filter: d.filter,
        textValue: d.textValue ?? undefined,
        integerValue: d.integerValue ?? undefined,
        filterValues: d.filterValues?.length ? d.filterValues : undefined,
        dateValue: d.dateValue ?? undefined,
    }));
}

/**
 * Compute the lowest variant price (used as referencePrice on marketplace).
 * Variants with no line price fall back via `fallbackByVariantId`, when provided.
 * Returns null if no prices are available.
 */
export function computeMarketplaceReferencePrice(
    data: CreateOfferData,
    fallbackByVariantId?: (sourceVariantId: number) => string | null,
): string | null {
    const variants = (data.filterData.variants || []) as (OfferVariantPost & { sourceVariantId?: number })[];
    if (!variants.length) {
        return null;
    }
    let lowest: Decimal | null = null;
    for (const v of variants) {
        const line = data.priceData.variants?.[v.code];
        const raw =
            line?.price != null
                ? line.price
                : v.sourceVariantId != null && fallbackByVariantId
                  ? fallbackByVariantId(v.sourceVariantId)
                  : null;
        if (raw == null) {
            continue;
        }
        const p = new Decimal(raw);
        if (lowest === null || p.lt(lowest)) {
            lowest = p;
        }
    }
    return lowest?.toFixed(2) ?? null;
}

/**
 * Build variants payload for the CREATE flow (full nested definitions, no id).
 */
export function buildCreateVariantsPayload(data: CreateOfferData, variants: OfferVariantPost[]): OfferVariantInterface[] {
    return variants.map((v) => ({
        definitions: v.definitions.map((d) => {
            const filter = data.filterData.filters.find((f) => f.id === d.filter);
            return {
                filter: d.filter,
                integerValue: filter.type === 'decimal' ? Math.ceil(d.value.integerValue * 100) : d.value.integerValue,
                textValue: d.value.textValue,
                filterValues: [d.value.filterValue],
            };
        }),
        price: data.priceData.variants[v.code]?.price || null,
        stocks: data.priceData.variants[v.code]?.stocks || null,
        sku: data.priceData.variants[v.code]?.sku || null,
    }));
}

/**
 * Build variants payload for the UPDATE flow: matched by `id` (sourceVariantId),
 * only price / stocks / sku. Falls back to the existing offer's values when the
 * form line is empty.
 */
export function buildUpdateVariantsPayload(
    data: CreateOfferData,
    existingVariants: OfferVariant[],
): Array<{ id: number; price: string | null; stocks: number; sku: string }> {
    return ((data.filterData.variants || []) as (OfferVariantPost & { sourceVariantId?: number })[]).map((post) => {
        const line = data.priceData.variants?.[post.code];
        const sourceId = post.sourceVariantId;
        const source = existingVariants.find((x) => x.id === sourceId);
        const price =
            line?.price != null ? new Decimal(line.price).toFixed(2) : source?.price != null ? new Decimal(source.price).toFixed(2) : null;
        return {
            id: sourceId,
            price,
            stocks: line?.stocks ?? source?.stocks ?? 0,
            sku: line?.sku ?? source?.sku,
        };
    });
}

// ---------------------------------------------------------------------------
// Facade builders
// ---------------------------------------------------------------------------

function isBlankVariantPrice(price: unknown): boolean {
    if (price == null) {
        return true;
    }
    if (typeof price === 'string') {
        return price.trim() === '';
    }
    if (typeof price === 'number') {
        return !Number.isFinite(price);
    }
    return false;
}

/** Marketplace create payload included a variant with no price. */
export class MissingVariantPriceError extends Error {
    constructor() {
        super('Each variant must have a price');
        this.name = 'MissingVariantPriceError';
    }
}

/**
 * Build the full OfferInterface body used to POST a new offer. When a variant
 * group is provided (multi-offer publication), its variants + medias drive the
 * payload.
 */
export function buildCreateOfferPayload(data: CreateOfferData, user: User | null, group: OfferVariantGroup | null = null): OfferInterface {
    const variants = group ? group.variants : data.filterData.variants || [];

    const offer: OfferInterface = {
        platform: data.platformData.platform,
        offerGroup: null,
        category: data.categoryData.category.id,
        name: data.filterData.name,
        description: data.filterData.description,
        referenceCurrency: normalizeCurrency(data.priceData.referenceCurrency) as string,
        vat: normalizeVatId(data.priceData.vat),
        address: data.addressData?.address?.id || null,
        country: data.addressData?.address?.country || user?.market || user?.country || getActiveMarket().country,
        definitions: [],
        sizeGuide: data.filterData.sizeGuide || null,
        variants: buildCreateVariantsPayload(data, variants),
        medias: buildMediaIds(data, group),
        documents: (data.filterData.documents ?? []).map((d) => ({
            type: d.type,
            media: d.mediaId,
            expiresAt: d.expiresAt ?? null,
        })),
        packages: buildPackagesPayload(data),
        referencePrice: null,
        daysToShip: data.priceData?.daysToShip || 2,
        // For gazette, we need to set a default stock of 1
        stocks:
            data.priceData?.stocks ||
            (data.platformData.platform === 'gazette' || data.platformData.platform === 'motors' || data.platformData.platform === 'estate'
                ? 1
                : 0),
    };

    if (!variants.length) {
        offer.sku = data.priceData?.sku ?? null;
    }

    switch (data.platformData.platform) {
        case 'wholesale': {
            const wholesale = buildWholesaleFields(data);
            offer.wholesalePrices = wholesale.wholesalePrices;
            offer.minWholesaleQuantity = wholesale.minWholesaleQuantity;
            offer.referencePrice = wholesale.referencePrice;
            break;
        }
        case 'marketplace': {
            let lowestPrice: string | null = null;
            for (const variant of offer.variants) {
                if (isBlankVariantPrice(variant.price)) {
                    throw new MissingVariantPriceError();
                }
                if (lowestPrice === null || new Decimal(variant.price).lt(lowestPrice)) {
                    lowestPrice = variant.price;
                }
            }
            offer.referencePrice = lowestPrice || data.priceData.referencePrice;
            break;
        }
        case 'gazette':
        case 'motors':
        case 'estate':
            offer.referencePrice = data.priceData.referencePrice;
            break;
        default: {
            const _exhaustive: never = data.platformData.platform;
            void _exhaustive;
            break;
        }
    }

    const shippable = CategoryUtils.findProperty(data.categoryData.category, 'shippable');
    offer.shippingSubsidyPercent = shippable ? data.priceData?.shippingSubsidyPercent ?? 0 : 0;

    offer.definitions = buildDefinitionsPayload(data, variants) as unknown as OfferDefinition[];

    return offer;
}

/**
 * Build PUT body used to update an existing offer. Only includes fields that
 * the update form accepts; price/marketplace logic mirrors the create flow.
 */
export function buildUpdateOfferPayload(data: CreateOfferData, offer: Offer, shippable: boolean): Record<string, unknown> {
    const pd = data.priceData;
    const wholesale = data.platformData.platform === 'wholesale' ? buildWholesaleFields(data) : null;

    let referencePrice: string | number | null = pd.referencePrice;
    if (wholesale) {
        referencePrice = wholesale.referencePrice;
    } else if ((data.filterData.variants?.length || 0) > 0 && offer.platform === 'marketplace') {
        const lowest = computeMarketplaceReferencePrice(data, (sourceId) => {
            const v = offer.variants.find((x) => x.id === sourceId);
            return v?.price ?? null;
        });
        if (lowest != null) {
            referencePrice = lowest;
        }
    } else if (referencePrice != null) {
        referencePrice = new Decimal(referencePrice).toFixed(2);
    }

    let mediaIds = buildMediaIds(data);
    if (mediaIds.length === 0) {
        mediaIds = (offer.medias ?? []).map((m) => m.id).filter((id): id is number => id != null);
    }

    const vatId = normalizeVatId(pd.vat) ?? normalizeVatId(offer.vat?.id);

    const body: Record<string, unknown> = {
        name: data.filterData.name,
        description: data.filterData.description,
        referencePrice,
        referenceCurrency: normalizeCurrency(pd.referenceCurrency),
        daysToShip: pd.daysToShip,
        stocks: pd.stocks,
        sku: pd.sku ?? offer.sku ?? null,
        variants: buildUpdateVariantsPayload(data, offer.variants),
        definitions: buildDefinitionsPayload(data),
    };

    if (vatId != null) {
        body['vat'] = vatId;
    }

    if (mediaIds.length > 0) {
        body['medias'] = mediaIds;
    }

    const documents = data.filterData.documents ?? [];
    if (documents.length > 0) {
        body['documents'] = documents.map((d) => ({
            type: d.type,
            media: d.mediaId,
            expiresAt: d.expiresAt ?? null,
        }));
    } else if ((offer.documents ?? []).length > 0) {
        body['documents'] = offer.documents.map((d) => ({
            type: d.type,
            media: d.media?.id,
            expiresAt: d.expiresAt ?? null,
        }));
    }

    if (shippable) {
        body['packages'] = buildPackagesPayload(data);
        body['address'] = data.addressData?.address?.id ?? null;
        body['shippingSubsidyPercent'] = pd.shippingSubsidyPercent ?? 0;
    } else if (data.addressData?.address?.id) {
        body['address'] = data.addressData.address.id;
    }

    if (wholesale) {
        body['wholesalePrices'] = wholesale.wholesalePrices;
        body['minWholesaleQuantity'] = wholesale.minWholesaleQuantity;
    }

    return body;
}

/**
 * Wholesale POST shape: sample line (`min: 1`, `max` = threshold), then the grid.
 * `referencePrice` is the lowest grid price. Shared by create and wholesale update.
 */
function buildWholesaleFields(data: CreateOfferData): {
    wholesalePrices: OfferWholesaleLine[];
    minWholesaleQuantity: number;
    referencePrice: string;
} {
    const grid = data.priceData.wholesalePrices;
    const lowest = grid.reduce((prev, curr) => (new Decimal(prev.price).lt(new Decimal(curr.price)) ? prev : curr));
    return {
        wholesalePrices: [
            {
                price: data.priceData.samplePrice,
                min: 1,
                max: data.priceData.minWholesaleQuantity,
            },
            ...grid.map((line) => ({
                price: new Decimal(line.price).toFixed(4),
                min: line.min,
                max: line.max,
            })),
        ],
        minWholesaleQuantity: data.priceData.minWholesaleQuantity,
        referencePrice: lowest.price,
    };
}
